// apps/web/src/pages/Admin/CollectionAvailabilityFields.jsx
//
// Admin "Availability" controls for a collection: Always available (default)
// or Seasonal / limited-time with an explicit start and end date + time,
// entered in a chosen timezone and stored as UTC.
//
// Window semantics (enforced by the backend): start inclusive, end exclusive.
// To make "through October 31" unambiguous the end is entered as the moment
// earning STOPS (e.g. Nov 1, 12:00 AM), and the summary spells out the last
// day users can earn. The server re-validates everything.
import React from 'react';
import {
    browserTimeZone,
    COMMON_TIME_ZONES,
    zonedTimeToUtc,
    formatDateTime,
} from './Campaigns/campaignUtils';
import { formatSeasonEnd } from '@aluvo/constants/src/collectionAvailability';
import styles from './AdminCollections.module.css';

export const EMPTY_AVAILABILITY = Object.freeze({
    mode: 'permanent', // 'permanent' | 'seasonal'
    seasonLabel: '',
    fromDate: '',
    fromTime: '00:00',
    untilDate: '',
    untilTime: '00:00',
    timezone: browserTimeZone(),
});

/** UTC instant → { date: 'YYYY-MM-DD', time: 'HH:mm' } in `timeZone`. */
export function utcToZonedParts(value, timeZone) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return { date: '', time: '00:00' };
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).formatToParts(date);
        const get = (type) => parts.find((p) => p.type === type)?.value;
        return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
    } catch {
        return { date: date.toISOString().slice(0, 10), time: date.toISOString().slice(11, 16) };
    }
}

/** Form state for an existing collection (API row, snake or camel case). */
export function availabilityFromCollection(collection, timezone = browserTimeZone()) {
    const isSeasonal = Boolean(collection?.is_seasonal ?? collection?.isSeasonal);
    if (!isSeasonal) return { ...EMPTY_AVAILABILITY, timezone };
    const from = utcToZonedParts(collection.available_from ?? collection.availableFrom, timezone);
    const until = utcToZonedParts(collection.available_until ?? collection.availableUntil, timezone);
    return {
        mode: 'seasonal',
        seasonLabel: collection.season_label ?? collection.seasonLabel ?? '',
        fromDate: from.date,
        fromTime: from.time,
        untilDate: until.date,
        untilTime: until.time,
        timezone,
    };
}

export function resolveWindow(form) {
    return {
        from: zonedTimeToUtc(form.fromDate, form.fromTime || '00:00', form.timezone),
        until: zonedTimeToUtc(form.untilDate, form.untilTime || '00:00', form.timezone),
    };
}

/** Client-side check (the server validates again and is authoritative). */
export function validateAvailability(form) {
    if (form.mode !== 'seasonal') return null;
    const { from, until } = resolveWindow(form);
    if (!from) return 'Choose when the collection becomes available.';
    if (!until) return 'Choose when the collection stops being available.';
    if (until.getTime() <= from.getTime()) return '“Available until” must be after “Available from”.';
    return null;
}

/** API payload. Permanent sends only is_seasonal:false — the server clears the dates. */
export function availabilityToPayload(form) {
    if (form.mode !== 'seasonal') return { is_seasonal: false };
    const { from, until } = resolveWindow(form);
    return {
        is_seasonal: true,
        available_from: from ? from.toISOString() : null,
        available_until: until ? until.toISOString() : null,
        season_label: form.seasonLabel.trim() || null,
    };
}

export function CollectionAvailabilityFields({ value, onChange, disabled = false, idPrefix = 'availability' }) {
    const set = (key, v) => onChange({ ...value, [key]: v });
    const seasonal = value.mode === 'seasonal';
    const { from, until } = seasonal ? resolveWindow(value) : { from: null, until: null };
    const zones = [...new Set([value.timezone, browserTimeZone(), ...COMMON_TIME_ZONES])];

    return (
        <fieldset className={styles.availabilityFieldset} disabled={disabled}>
            <legend className={styles.itemsTitle}>Availability</legend>
            <div className={styles.availabilityModes} role="radiogroup" aria-label="Availability">
                <label className={styles.toggleLabel}>
                    <input
                        type="radio"
                        name={`${idPrefix}-mode`}
                        value="permanent"
                        checked={!seasonal}
                        onChange={() => set('mode', 'permanent')}
                    />
                    <span>Always available</span>
                </label>
                <label className={styles.toggleLabel}>
                    <input
                        type="radio"
                        name={`${idPrefix}-mode`}
                        value="seasonal"
                        checked={seasonal}
                        onChange={() => set('mode', 'seasonal')}
                    />
                    <span>Seasonal / limited-time</span>
                </label>
            </div>

            {seasonal && (
                <>
                    <p className={styles.itemsHint}>
                        Users can earn this collection coin only during this window. Coins already earned remain
                        permanently owned, and titles verified before the window opens still count.
                    </p>
                    <div className={styles.coinFormGrid}>
                        <div className={styles.itemField} style={{ flex: '2 1 220px' }}>
                            <label className={styles.labelSmall} htmlFor={`${idPrefix}-label`}>Season label (optional)</label>
                            <input
                                id={`${idPrefix}-label`}
                                className={styles.inputSmall}
                                type="text"
                                maxLength={80}
                                placeholder="e.g. Halloween 2026"
                                value={value.seasonLabel}
                                onChange={(e) => set('seasonLabel', e.target.value)}
                            />
                        </div>
                        <div className={styles.itemField} style={{ flex: '1 1 200px' }}>
                            <label className={styles.labelSmall} htmlFor={`${idPrefix}-tz`}>Timezone</label>
                            <select
                                id={`${idPrefix}-tz`}
                                className={styles.selectSmall}
                                value={value.timezone}
                                onChange={(e) => set('timezone', e.target.value)}
                            >
                                {zones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className={styles.coinFormGrid}>
                        <div className={styles.itemField}>
                            <label className={styles.labelSmall} htmlFor={`${idPrefix}-from-date`}>Available from</label>
                            <input
                                id={`${idPrefix}-from-date`}
                                className={styles.inputSmall}
                                type="date"
                                value={value.fromDate}
                                onChange={(e) => set('fromDate', e.target.value)}
                            />
                        </div>
                        <div className={styles.itemField}>
                            <label className={styles.labelSmall} htmlFor={`${idPrefix}-from-time`}>Start time</label>
                            <input
                                id={`${idPrefix}-from-time`}
                                className={styles.inputSmall}
                                type="time"
                                value={value.fromTime}
                                onChange={(e) => set('fromTime', e.target.value)}
                            />
                        </div>
                        <div className={styles.itemField}>
                            <label className={styles.labelSmall} htmlFor={`${idPrefix}-until-date`}>Available until</label>
                            <input
                                id={`${idPrefix}-until-date`}
                                className={styles.inputSmall}
                                type="date"
                                value={value.untilDate}
                                onChange={(e) => set('untilDate', e.target.value)}
                            />
                        </div>
                        <div className={styles.itemField}>
                            <label className={styles.labelSmall} htmlFor={`${idPrefix}-until-time`}>End time</label>
                            <input
                                id={`${idPrefix}-until-time`}
                                className={styles.inputSmall}
                                type="time"
                                value={value.untilTime}
                                onChange={(e) => set('untilTime', e.target.value)}
                            />
                        </div>
                    </div>
                    <p className={styles.itemsHint}>
                        The end is the moment earning <strong>stops</strong>. To run through the end of October 31,
                        choose November 1 at 12:00 AM.
                    </p>
                    {from && until && until > from && (
                        <p className={styles.availabilitySummary} data-testid="availability-summary">
                            Earnable from {formatDateTime(from, value.timezone)} until {formatDateTime(until, value.timezone)}
                            {' '}— last day: <strong>{formatSeasonEnd(until, { timeZone: value.timezone })}</strong>.
                            {' '}Stored as UTC {from.toISOString().replace('.000Z', 'Z')} → {until.toISOString().replace('.000Z', 'Z')}.
                        </p>
                    )}
                </>
            )}
        </fieldset>
    );
}

export default CollectionAvailabilityFields;
